import axios from 'axios';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
  timeout: 30000, // 30 seconds timeout (reduced from 60s)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Skip if Authorization header is already set (manually set by authStore)
    if (config.headers.Authorization) {
      return config;
    }

    let accessToken = null;

    // Try to get token from Zustand store (emt-crm-auth)
    try {
      const zustandData = localStorage.getItem('emt-crm-auth');
      if (zustandData) {
        const parsedZustand = JSON.parse(zustandData);
        accessToken = parsedZustand?.state?.session?.access_token;
      }
    } catch (error) {
      console.warn('Failed to parse Zustand auth data:', error);
    }

    // Fallback to Supabase's own storage (supabase-auth)
    if (!accessToken) {
      try {
        const supabaseData = localStorage.getItem('supabase-auth');
        if (supabaseData) {
          const parsedSupabase = JSON.parse(supabaseData);
          // Supabase stores session in different formats, try to extract access_token
          if (parsedSupabase?.access_token) {
            accessToken = parsedSupabase.access_token;
          } else if (parsedSupabase?.currentSession?.access_token) {
            accessToken = parsedSupabase.currentSession.access_token;
          } else if (typeof parsedSupabase === 'object') {
            // Try to find access_token anywhere in the object
            const findAccessToken = (obj) => {
              if (obj?.access_token) return obj.access_token;
              for (const key in obj) {
                if (typeof obj[key] === 'object' && obj[key] !== null) {
                  const found = findAccessToken(obj[key]);
                  if (found) return found;
                }
              }
              return null;
            };
            accessToken = findAccessToken(parsedSupabase);
          }
        }
      } catch (error) {
        console.warn('Failed to parse Supabase auth data:', error);
      }
    }

    // Set Authorization header if token found
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors with retry
    if (!error.response && !originalRequest._retry) {
      originalRequest._retry = true;
      console.warn('Network error, retrying...', error.message);
      
      // Wait 1 second before retry
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        return api(originalRequest);
      } catch (retryError) {
        console.error('Retry failed:', retryError.message);
        return Promise.reject({
          ...error,
          message: 'Ağ bağlantı hatası. Lütfen sayfayı yenileyin.'
        });
      }
    }

    // Handle specific HTTP status codes
    if (error.response) {
      switch (error.response.status) {
        case 401:
          console.warn('Unauthorized - redirecting to login');
          // Don't redirect here - let the auth store handle it
          break;
        case 500:
          console.error('Server error:', error.response.data);
          break;
        default:
          console.warn('API error:', error.response.status, error.response.data);
      }
    }

    return Promise.reject(error);
  }
);

// Helper functions for common API operations
export const apiHelpers = {
  // GET request with query parameters
  get: (url, params = {}) => {
    return api.get(url, { params });
  },

  // POST request
  post: (url, data = {}) => {
    return api.post(url, data);
  },

  // PUT request
  put: (url, data = {}) => {
    return api.put(url, data);
  },

  // PATCH request
  patch: (url, data = {}) => {
    return api.patch(url, data);
  },

  // DELETE request
  delete: (url) => {
    return api.delete(url);
  },

  // Upload file with progress tracking
  upload: (url, formData, onProgress = null) => {
    return api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        const progress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(progress);
      } : undefined,
    });
  },

  // Download file
  download: (url, filename) => {
    return api.get(url, {
      responseType: 'blob',
    }).then(response => {
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    });
  }
};

// Export the main api instance
export { api };

// API endpoints
export const endpoints = {
  // Auth endpoints
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
    register: '/auth/register',
    changePassword: '/auth/change-password',
    updateProfile: '/auth/profile',
    institutions: '/auth/institutions'
  },

  // Cases endpoints
  cases: {
    list: '/cases',
    create: '/cases',
    createStage1: '/cases/stage1',
    get: (id) => `/cases/${id}`,
    getById: (id) => `/cases/${id}`,
    update: (id) => `/cases/${id}`,
    delete: (id) => `/cases/${id}`,
    updateStage2: (id) => `/cases/${id}/stage2`,
    updateStage3: (id) => `/cases/${id}/stage3`,
    uploadDocument: (id) => `/cases/${id}/upload-document`,
    getDocument: (id) => `/cases/${id}/document`,
    deleteDocument: (id) => `/cases/${id}/document`,
    uploadAsosDocument: (id) => `/cases/${id}/upload-asos-document`,
    getAsosDocument: (id) => `/cases/${id}/asos-document`,
    deleteAsosDocument: (id) => `/cases/${id}/asos-document`,
    uploadEk3Document: (id) => `/cases/${id}/upload-ek3-document`,
    getEk3Document: (id) => `/cases/${id}/ek3-document`,
    deleteEk3Document: (id) => `/cases/${id}/ek3-document`,
    institutions: '/cases/lookup/institutions',
    hospitals: '/cases/lookup/hospitals',
    sourceHospitals: '/cases/lookup/hospitals/source',
    destinationHospitals: '/cases/lookup/hospitals/destination',
    teams: '/cases/lookup/teams',
    stats: '/cases/stats'
  },

  // Users endpoints
  users: {
    list: '/users',
    create: '/users',
    get: (id) => `/users/${id}`,
    update: (id) => `/users/${id}`,
    delete: (id) => `/users/${id}`
  },

  // Institutions endpoints
  institutions: {
    list: '/institutions',
    create: '/institutions',
    get: (id) => `/institutions/${id}`,
    update: (id) => `/institutions/${id}`,
    delete: (id) => `/institutions/${id}`
  },

  // Organizations endpoints
  organizations: {
    list: '/organizations',
    create: '/organizations',
    get: (id) => `/organizations/${id}`,
    update: (id) => `/organizations/${id}`,
    delete: (id) => `/organizations/${id}`,
    copy: (id) => `/organizations/${id}/copy`,
    cancel: (id) => `/organizations/${id}/cancel`,
    types: '/organizations/lookup/types',
    active: '/organizations/lookup/active'
  },

  // Teams endpoints
  teams: {
    list: '/teams',
    create: '/teams',
    get: (id) => `/teams/${id}`,
    update: (id) => `/teams/${id}`,
    delete: (id) => `/teams/${id}`,
    daily: '/teams/daily'
  },

  // Hospitals endpoints
  hospitals: {
    list: '/hospitals',
    create: '/hospitals',
    get: (id) => `/hospitals/${id}`,
    update: (id) => `/hospitals/${id}`,
    delete: (id) => `/hospitals/${id}`
  },

  // Medical definitions endpoints
  medical: {
    diagnoses: '/medical/diagnoses',
    medications: '/medical/medications',
    supplies: '/medical/supplies',
  },

  // Lookup definitions endpoints (hastane adresleri, sevk turleri)
  lookups: {
    addresses: '/lookups/addresses',
    transportTypes: '/lookups/transport-types',
  },

  // Dashboard endpoints
  dashboard: {
    stats: '/dashboard/stats',
    recentCases: '/dashboard/recent-cases',
    charts: '/dashboard/charts'
  },

  // Excel endpoints
  excel: {
    template: '/excel/template',
    import: '/excel/import',
    export: '/excel/export'
  },

  // Quotes endpoints
  quotes: {
    list: '/quotes',
    create: '/quotes',
    get: (id) => `/quotes/${id}`,
    update: (id) => `/quotes/${id}`,
    statusUpdate: (id) => `/quotes/${id}/status`,
    history: (id) => `/quotes/${id}/history`,
    pdf: (id) => `/quotes/${id}/pdf`,
    cities: '/quotes/cities',
    districts: (city) => `/quotes/districts/${city}`,
    distanceLookup: '/quotes/distance-lookup',
    calculate: '/quotes/calculate',
  },

  // Arvento / vehicle tracking endpoints
  arvento: {
    teamLocation: (teamId) => `/arvento/team-location/${teamId}`,
  }
};

// Users API (Admin only)
export const usersApi = {
  getAll: (params = {}) => {
    const queryString = params ? `?${new URLSearchParams(params)}` : '';
    return api.get(`/users${queryString}`);
  },
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  changePassword: (id, password) => api.put(`/users/${id}/password`, { password })
};

// Personnel API
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Zustand store'dan access token (img src ?token= için)
export const getAccessToken = () => {
  try {
    const z = JSON.parse(localStorage.getItem('emt-crm-auth') || '{}');
    return z?.state?.session?.access_token || null;
  } catch {
    return null;
  }
};

export const personnelApi = {
  list: (params = {}) => api.get(`/personnel?${new URLSearchParams(params)}`),
  // Form alanlarinda ad-soyad secimi icin hafif liste (yonetim yetkisi gerektirmez)
  lookup: () => api.get('/personnel/lookup/list'),
  get: (id) => api.get(`/personnel/${id}`),
  create: (data) => api.post('/personnel', data),
  update: (id, data) => api.put(`/personnel/${id}`, data),
  remove: (id, deleteUser = false) => api.delete(`/personnel/${id}?delete_user=${deleteUser}`),
  templateUrl: () => `${API_BASE_URL}/personnel/io/template`,
  import: (formData) => api.post('/personnel/io/import', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),
  listDocuments: (id) => api.get(`/personnel/${id}/documents`),
  uploadDocument: (id, formData) => api.post(`/personnel/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument: (id, docId) => api.delete(`/personnel/${id}/documents/${docId}`),
  documentDownloadUrl: (id, docId) => `${API_BASE_URL}/personnel/${id}/documents/${docId}/download?token=${getAccessToken() || ''}`,
  photoUrl: (id) => `${API_BASE_URL}/personnel/${id}/photo?token=${getAccessToken() || ''}`,
  createUser: (id, data) => api.post(`/personnel/${id}/create-user`, data),
  bulkRole: (data) => api.post('/personnel/bulk-role', data, { timeout: 120000 }),
  matchDailyAssignments: () => api.get('/personnel/match/daily-assignments'),
  merge: (data) => api.post('/personnel/merge', data),
};

// Attendance (QR giriş-çıkış) API
export const attendanceApi = {
  getQr: (scope) => api.get(`/attendance/qr?scope=${encodeURIComponent(scope)}`),
  checkin: (data) => api.post('/attendance/checkin', data),
  list: (params = {}) => api.get(`/attendance?${new URLSearchParams(params)}`),
  myStatus: () => api.get('/attendance/my-status'),
};

// Puantaj / Nöbet API
export const puantajApi = {
  getCodes: () => api.get('/puantaj/codes'),
  addCode: (data) => api.post('/puantaj/codes', data),
  deleteCode: (id) => api.delete(`/puantaj/codes/${id}`),
  getMatrix: (month) => api.get(`/puantaj?month=${month}`),
  setCell: (data) => api.put('/puantaj/cell', data),
  exportUrl: (month) => `${API_BASE_URL}/puantaj/export?month=${month}&token=${getAccessToken() || ''}`,
  me: (month) => api.get(`/puantaj/me?month=${month}`),
  import: (formData) => api.post('/puantaj/import', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }),
  dayTeams: (date) => api.get(`/puantaj/day-teams?date=${date}`),
};

// Institutions API
export const institutionsApi = {
  getAll: (params = {}) => {
    const queryString = params ? `?${new URLSearchParams(params)}` : '';
    return api.get(`/institutions${queryString}`);
  }
};

export default api; 